import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, FlatList,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../../../../constants/Colors';
import Style from '../../../../../../constants/Style';
import SpaceItem from './SpaceItem';
import NoSpacesImage from '../../../NoSpacesImage';
import { Space } from '../../../../../../components/Map/SetFloorsModal/FloorItem';
import ScreenEmptyState from '../../../../../../components/ScreenEmptyState';
import { UserPermission } from '../../../../../../constants/Types';

interface SpaceListProps {
  spaces: Space[],
  parentName: string,
  setSelectedSpaceId: Dispatch<SetStateAction<string>>,
  removeSpace: Function,
  updateSpace: Function,
  showScreenAlert: Function,
  parentId: string,
  navigateToAddSpace: () => void,
  buildingId: string,
}

const SpaceList = ({
  spaces, navigateToAddSpace, parentName, removeSpace, updateSpace, setSelectedSpaceId, parentId,
  showScreenAlert, buildingId,
}: SpaceListProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canAddLocations: boolean = isAdmin || permissions.includes(UserPermission.LOCATIONS_CREATE);
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const { id }: { id: string, name: string } = useSelector((state:
    { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding);
  const actionsShown: boolean = canUpdateLocations || canDeleteLocations || id === buildingId;

  return <View style={styles.container}>
    <View style={[Style.row, styles.header]}>
      <Text style={[styles.parameterTitle, styles.titleSection]}>{'SPACES'}</Text>
      <Text style={[styles.parameterTitle, styles.prioritySection]}>{'PRIORITY'}</Text>
      <Text style={[styles.parameterTitle, styles.assetsSection]}>{'ASSETS'}</Text>
      {happyEnabled
        && <Text style={[styles.parameterTitle, styles.assetsSection]}>{'SENSORS & METERS'}</Text>}
      {actionsShown
        && <Text style={[styles.parameterTitle, styles.actionSection]}>{'ACTIONS'}</Text>}
    </View>
    <FlatList
      data={spaces?.filter((space: Space | null) => !!space)
        .sort((a: Space, b: Space) => a.Name.localeCompare(b.Name))}
      renderItem={({ item: space }) => <SpaceItem
        floorId={parentId}
        {...{
          space,
          parentName,
          removeSpace,
          updateSpace,
          setSelectedSpaceId,
          showScreenAlert,
          isAdmin,
          buildingId,
        }}
      />}
      keyExtractor={(space: Space) => `space${space?.LocationId}`}
      ListEmptyComponent={<ScreenEmptyState
        image={<NoSpacesImage />}
        title='No spaces in this area'
        subtitle='Areas and spaces are created on the map'
        adminSubtitle={'Areas and spaces are created on the map.\nAdd a space on the map to have it appear in this list'}
        buttonTitle='Go to map'
        buttonAction={navigateToAddSpace}
        actionAvailable={canAddLocations}
      />}
      style={styles.list}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 16,
    marginBottom: 8,
    marginHorizontal: 24,
  },
  parameterTitle: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    flex: 1,
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  titleSection: { flex: 0.5 },
  prioritySection: { flex: 0.12 },
  assetsSection: { flex: 0.09 },
  actionSection: {
    flex: 0.2,
    alignSelf: 'flex-end',
    textAlign: 'right',
  },
  list: {
    paddingHorizontal: 24,
    flex: 1,
  },
});

export default SpaceList;
